import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { isAuthRoute } from '@utils/auth';
import { closeTransientOverlays } from '@stores/transientOverlays';

/**
 * Closes every root-mounted overlay whenever the app lands on an auth route.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this exists because the door hop changed
 * what a sign-in IS. Pre-flag, `signIn()` ended in a full-page redirect to
 * Logto's hosted UI, so the document went away and with it every overlay on
 * screen. `routeToDoor` (useSignInOut / useAuthNavigation) replaced that with a
 * client-side `navigate(AUTH_DOOR_PATH)`, and the overlay HOSTS in index.tsx --
 * GlobalShareSheet, GlobalOnboarding, GlobalActivationDrawers -- are mounted
 * OUTSIDE <RouterProvider> precisely so a navigation cannot unmount them. The
 * two facts together mean an overlay open at hop time survives onto the door.
 *
 * The reported instance was the SpaceWelcome guest drawer: its last step's CTA
 * calls signIn(), GlobalOnboarding deliberately does NOT close the drawer on a
 * step action (the comment there says "the action will navigate away"), and
 * OnboardingCarousel latches `isActionPending` for the spinner. Post-hop the
 * door rendered behind a drawer nobody could dismiss, CTA spinning "One sec"
 * forever.
 *
 * Fixing it in the drawer (an onSignInSettled that closes) would fix one
 * caller. This is keyed on the DESTINATION instead, so it holds for every
 * producer -- the explicit-CTA hop, useAuthNavigation's auto-guard, a magic
 * link opened into a live tab at /auth/link, a bare typed URL, Back/Forward --
 * and for every overlay, including ones added later.
 *
 * Scope is `isAuthRoute` (the shared predicate, so this cannot drift from the
 * redirect-storage and auto-guard rules): /auth, /auth/link, /signup,
 * /callback, /logout. Nothing about an overlay from the previous screen belongs
 * on any of them.
 *
 * Mounted in RootLayout because it needs router context; the stores it drives
 * do not, which is what lets one in-router component reach four out-of-router
 * hosts.
 *
 * WHICH overlays is deliberately not stated here any more. This component used
 * to hand-name three `getState()` calls, which was complete on the day it was
 * written and had no way of staying complete: the next store to acquire a
 * persistent open state would ride onto `/auth` exactly as the welcome drawer
 * did, and nothing would fail. `@stores/transientOverlays` owns the list and
 * `transientOverlays.test.ts` scans the store directory against it, so a store
 * that is neither registered nor explicitly exempted is a red test. That file
 * also records why the non-store hosts (MaintenanceTakeover, the Toaster,
 * SignedOutByOtherTabDrawer, InstallPromptProvider) and the exempt stores
 * (celebrationStore) are not part of this and must not be.
 */
export function AuthRouteOverlayReset() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isAuthRoute(pathname)) {
      return;
    }

    closeTransientOverlays();
  }, [pathname]);

  return null;
}
